import {
	autoUpdate,
	useDismiss,
	flip,
	offset,
	shift,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
	useRole,
	useTransitionStatus,
	arrow,
} from "@floating-ui/react"
import type { Placement } from "@floating-ui/react"
import { useMemo, useRef, useState } from "react"

export interface UseTooltipOptions {
	placement?: Placement

	/**
	 * Whether the tooltip is enabled, i.e. has content to show. When disabled, the underlying
	 * floating-ui Effects and event listeners are skipped to avoid unnecessary work, while the hook
	 * still returns a stable shape so the component tree using it doesn't change across renders.
	 * @default true
	 */
	enabled?: boolean
}

export function useTooltip({ enabled = true, placement = "top" }: UseTooltipOptions = {}) {
	const [isOpen, setIsOpen] = useState(false)
	const arrowRef = useRef(null)

	// Adjust state during render (rather than in an Effect) when `enabled` flips to false, so a
	// stale `isOpen` doesn't let the tooltip pop open again once `enabled` becomes true again
	// without a new interaction. See https://react.dev/learn/you-might-not-need-an-effect
	const [isPreviouslyEnabled, setIsPreviouslyEnabled] = useState(enabled)
	if (enabled !== isPreviouslyEnabled) {
		setIsPreviouslyEnabled(enabled)
		if (!enabled && isOpen) {
			setIsOpen(false)
		}
	}

	const floating = useFloating({
		middleware: [
			offset(10),
			flip({
				crossAxis: placement.includes("-"),
				fallbackAxisSideDirection: "start",
				padding: 5,
			}),
			shift({ padding: 5 }),
			// eslint-disable-next-line react-hooks/refs
			arrow({ element: arrowRef }),
		],
		onOpenChange: setIsOpen,
		open: isOpen,
		placement,
		strategy: "fixed",
		whileElementsMounted: enabled ? autoUpdate : undefined,
	})

	const { context } = floating

	const transitionStatus = useTransitionStatus(context)
	const hover = useHover(context, { enabled, move: false })
	const focus = useFocus(context, { enabled })
	const dismiss = useDismiss(context, { enabled })
	const role = useRole(context, { enabled, role: "tooltip" })

	const interactions = useInteractions([hover, focus, dismiss, role])

	return useMemo(
		() => ({
			arrowRef,
			floating,
			interactions,
			open: isOpen,
			setOpen: setIsOpen,
			transitionStatus,
		}),
		[interactions, isOpen, floating, transitionStatus],
	)
}
